import { Choice } from "./Choice";
import { DeferredError } from "./DeferredError";
import { diagnostics } from "./Diag";
import { Errors } from "./Errors";
import { mhaStrings } from "./mhaStrings";
import { Poster } from "./Poster";
import { Strings } from "./Strings";
import { TabNavigation } from "./TabNavigation";
import { GetHeaders } from "./ui/getHeaders/GetHeaders";
import { ParentFrameUtils } from "./utils/ParentFrameUtils";

// Fluent UI Web Components interfaces
interface FluentDialog extends HTMLElement {
    dialog: HTMLDialogElement;
    show(): void;
    hide(): void;
    addEventListener(type: "dismiss", listener: (event: Event) => void): void;
    addEventListener(type: string, listener: EventListenerOrEventListenerObject, options?: boolean | AddEventListenerOptions): void;
}

interface FluentRadioGroup extends HTMLElement {
    value: string;
}

interface FluentRadio extends HTMLElement {
    value: string;
    checked: boolean;
}

interface FluentCheckbox extends HTMLElement {
    checked: boolean;
}

export class ParentFrame {
    private static iFrame: Window | null;
    private static currentChoice = {} as Choice;
    private static deferredErrors: DeferredError[] = [];
    private static deferredStatus: string[] = [];
    private static headers = "";
    private static modelToString = "";
    protected static telemetryCheckbox: FluentCheckbox | null = null;

    private static choices: Array<Choice> = [
        { label: "classic", url: "classicDesktopFrame.html", checked: false },
        { label: "new", url: "newDesktopFrame.html", checked: true },
        { label: "new-mobile", url: "newMobilePaneIosFrame.html", checked: false }
    ];

    private static setDefault(): void {
        let uiDefault: string = ParentFrameUtils.getQueryVariable("default");
        if (uiDefault === null) {
            uiDefault = "new";
        }

        ParentFrameUtils.setDefaultChoice(ParentFrame.choices, uiDefault);
    }

    private static postMessageToFrame(eventName: string, data: string | { error: string, message: string }): void {
        if (ParentFrame.iFrame) {
            Poster.postMessageToFrame(ParentFrame.iFrame, eventName, data);
        }
    }

    private static render(): void {
        if (ParentFrame.headers) diagnostics.trackEvent({ name: "analyzeHeaders" });
        ParentFrame.postMessageToFrame("renderItem", ParentFrame.headers);
    }

    private static setFrame(frame: Window): void {
        ParentFrame.iFrame = frame;
        TabNavigation.setIFrame(frame);

        if (ParentFrame.iFrame) {
            // If we have any deferred status, signal them
            ParentFrame.deferredStatus.forEach((status: string) => {
                ParentFrame.postMessageToFrame("updateStatus", status);
            });

            // Clear out the now displayed status
            ParentFrame.deferredStatus = [];

            // If we have any deferred errors, signal them
            ParentFrame.deferredErrors.forEach((deferredError: DeferredError) => {
                ParentFrame.postMessageToFrame("showError",
                    {
                        error: JSON.stringify(deferredError.error),
                        message: deferredError.message
                    });
            });

            // Clear out the now displayed errors
            ParentFrame.deferredErrors = [];

            ParentFrame.render();
        }
    }

    private static eventListener(event: MessageEvent): void {
        if (!event || event.origin !== Poster.site()) return;

        if (event.data) {
            switch (event.data.eventName) {
                case "frameActive":
                    ParentFrame.setFrame(event.source as Window);
                    break;
                case "LogError":
                    Errors.log(JSON.parse(event.data.data.error), event.data.data.message);
                    break;
                case "modelToString":
                    ParentFrame.modelToString = event.data.data;
                    break;
            }
        }
    }

    private static async loadNewItem() {
        if (Office.context.mailbox.item) {
            await GetHeaders.send(function (headers: string, apiUsed: string): void {
                ParentFrame.headers = headers;
                diagnostics.set("API used", apiUsed);
                ParentFrame.render();
            });
        }
    }

    private static registerItemChangedEvent(): void {
        try {
            if (Office.context.mailbox.addHandlerAsync !== undefined) {
                Office.context.mailbox.addHandlerAsync(Office.EventType.ItemChanged,
                    function (): void {
                        Errors.clear();
                        diagnostics.clear();
                        ParentFrame.loadNewItem();
                    });
            }
        } catch (e) {
            Errors.log(e, "Could not register item changed event");
        }
    }

    // Tells the UI to show an error.
    public static showError(error: unknown, message: string, suppressTracking?: boolean): void {
        Errors.log(error, message, suppressTracking);

        if (ParentFrame.iFrame) {
            ParentFrame.postMessageToFrame("showError", { error: JSON.stringify(error), message: message });
        } else {
            // We don't have an iFrame, so defer the message
            ParentFrame.deferredErrors.push(<DeferredError>{ error: error, message: message });
        }
    }

    // Tells the UI to show an error.
    public static updateStatus(statusText: string): void {
        if (ParentFrame.iFrame) {
            ParentFrame.postMessageToFrame("updateStatus", statusText);
        } else {
            // We don't have an iFrame, so defer the status
            ParentFrame.deferredStatus.push(statusText);
        }
    }

    // Display primary UI
    private static go(choice: Choice): void {
        ParentFrame.iFrame = null;
        ParentFrame.currentChoice = choice;
        (document.getElementById("uiFrame") as HTMLIFrameElement).src = choice.url;
        if (Office.context) {
            Office.context.roamingSettings.set(ParentFrameUtils.getSettingsKey(), choice);
            Office.context.roamingSettings.saveAsync();
        }
    }

    private static goDefaultChoice(): void {
        const choice = ParentFrame.choices.find((c: Choice) => c.checked);
        if (choice) {
            ParentFrame.go(choice);
        }
    }

    // Create list of choices to display for the UI types
    private static addChoices(): void {
        const radioGroup = document.getElementById("uiChoice") as FluentRadioGroup;
        if (!radioGroup) return;

        // Clear existing options
        radioGroup.innerHTML = "";

        ParentFrame.choices.forEach((choice: Choice, iChoice: number) => {
            const label = document.createElement("label");
            label.className = "fluent-control-label";
            const radio = document.createElement("fluent-radio") as FluentRadio;
            radio.value = iChoice.toString();

            if (choice.checked) {
                radio.checked = true;
            }

            label.append(radio, choice.label);
            radioGroup.appendChild(label);
        });
    }

    // Hook the UI together for display
    private static initFluent(): void {
        const header: Element | null = document.querySelector(".header-row");
        if (!header) return;

        const dialogSettings = document.getElementById("dialog-Settings") as FluentDialog;
        const dialogDiagnostics = document.getElementById("dialog-Diagnostics") as FluentDialog;

        if (!dialogSettings || !dialogDiagnostics) return;

        dialogSettings.hidden = false;
        dialogDiagnostics.hidden = false;

        // Add click-outside-to-dismiss functionality
        dialogSettings.addEventListener("click", (e) => {
            if (e.target === dialogSettings) {
                dialogSettings.hide();
            }
        });

        dialogDiagnostics.addEventListener("click", (e) => {
            if (e.target === dialogDiagnostics) {
                dialogDiagnostics.hide();
            }
        });

        // Add escape key to dismiss dialogs and enter key to apply settings
        document.addEventListener("keydown", (e) => {
            if (e.key === "Escape") {
                if (dialogSettings.dialog.open) {
                    dialogSettings.hide();
                }

                if (dialogDiagnostics.dialog.open) {
                    dialogDiagnostics.hide();
                }
            }

            if (e.key === "Enter" && dialogSettings.dialog.open) {
                // Only trigger OK action when focused on radio buttons or checkboxes
                const activeElement = document.activeElement;
                const isRadioOrCheckbox = activeElement &&
                    (activeElement.tagName.toLowerCase() === "fluent-radio" ||
                        activeElement.tagName.toLowerCase() === "fluent-checkbox");

                if (isRadioOrCheckbox) {
                    // Trigger the same action as the OK button
                    const radioGroup = document.getElementById("uiChoice") as FluentRadioGroup;
                    if (radioGroup && radioGroup.value) {
                        const iChoice = parseInt(radioGroup.value);
                        const choice: Choice | undefined = ParentFrame.choices[iChoice];
                        if (choice && choice.label !== ParentFrame.currentChoice.label) {
                            ParentFrame.go(choice);
                        }
                    }

                    // Update telemetry setting
                    if (ParentFrame.telemetryCheckbox) {
                        diagnostics.setSendTelemetry(ParentFrame.telemetryCheckbox.checked);
                    }

                    dialogSettings.hide();
                    e.preventDefault(); // Prevent default form submission behavior
                }
            }

            if (e.key === "Enter" && dialogDiagnostics.dialog.open) {
                // Close diagnostics dialog when Enter is pressed on the code box
                const activeElement = document.activeElement;
                if (activeElement && activeElement.id === "diagpre") {
                    dialogDiagnostics.hide();
                    e.preventDefault();
                }
            }
        });

        const telemetryCheckbox = document.getElementById("telemetryInput") as FluentCheckbox;
        if (telemetryCheckbox) {
            ParentFrame.telemetryCheckbox = telemetryCheckbox;
            ParentFrame.setSendTelemetryUI(diagnostics.canSendTelemetry());
        }

        // Wire up action buttons
        const okButton = document.getElementById("actionsSettings-OK");
        okButton?.addEventListener("click", () => {
            // Get selected choice from radio group
            const radioGroup = document.getElementById("uiChoice") as FluentRadioGroup;
            if (radioGroup && radioGroup.value) {
                const iChoice = parseInt(radioGroup.value);
                const choice: Choice | undefined = ParentFrame.choices[iChoice];
                if (choice && choice.label !== ParentFrame.currentChoice.label) {
                    ParentFrame.go(choice);
                }
            }

            // Update telemetry setting
            if (ParentFrame.telemetryCheckbox) {
                diagnostics.setSendTelemetry(ParentFrame.telemetryCheckbox.checked);
            }

            dialogSettings.hide();
        });

        const diagButton = document.getElementById("actionsSettings-diag");
        diagButton?.addEventListener("click", () => {
            const diagnosticsText = ParentFrameUtils.getDiagnosticsString();
            const diagnosticsElement = document.getElementById("diagnostics");
            if (diagnosticsElement) {
                diagnosticsElement.textContent = diagnosticsText;
            }

            // Hide settings dialog and show diagnostics dialog
            dialogSettings.hide();
            dialogDiagnostics.show();
            document.getElementById("diagpre")?.focus();
        });

        const diagOkButton = document.getElementById("actionsDiag-OK");
        diagOkButton?.addEventListener("click", () => {
            dialogDiagnostics.hide();
        });

        const settingsButton = document.getElementById("settingsButton");
        settingsButton?.addEventListener("click", () => {
            // Set the current choice in the radio group
            const radioGroup = document.getElementById("uiChoice") as FluentRadioGroup;
            if (radioGroup) {
                const currentIndex = ParentFrame.choices.findIndex(c => c.label === ParentFrame.currentChoice.label);
                if (currentIndex >= 0) {
                    radioGroup.value = currentIndex.toString();
                }
            }

            dialogSettings.show();
        });

        const copyButton = document.getElementById("copyButton");
        copyButton?.addEventListener("click", () => {
            Strings.copyToClipboard(ParentFrame.modelToString);

            // Show status message for accessibility
            const statusMessage = document.getElementById("statusMessage");
            if (statusMessage) {
                statusMessage.textContent = mhaStrings.mhaCopied;
                statusMessage.classList.add("show");

                // Hide after 2 seconds
                setTimeout(() => {
                    statusMessage.classList.remove("show");
                    statusMessage.textContent = "";
                }, 2000);
            }

            // Maintain focus on copy button for keyboard users
            copyButton.focus();
        });

        // Initialize tab navigation handling
        TabNavigation.initialize();
    }

    public static setSendTelemetryUI(sendTelemetry: boolean) {
        if (ParentFrame.telemetryCheckbox) {
            ParentFrame.telemetryCheckbox.checked = sendTelemetry;
        }
    }

    public static async initUI() {
        ParentFrame.setDefault();
        ParentFrame.addChoices();
        ParentFrame.initFluent();

        try {
            const choice: Choice = Office.context.roamingSettings.get(ParentFrameUtils.getSettingsKey());
            const sendTelemetry: boolean = Office.context.roamingSettings.get("sendTelemetry");
            diagnostics.initSendTelemetry(sendTelemetry);

            ParentFrame.go(choice);
        } catch {
            ParentFrame.goDefaultChoice();
        }

        ParentFrame.registerItemChangedEvent();

        window.addEventListener("message", ParentFrame.eventListener, false);
        await ParentFrame.loadNewItem();
    }

    public static get choice(): Choice { return ParentFrame.currentChoice; }
}
